import { useFeatureIsOn } from 'growthbook'
import * as React from 'react'
import { lazyComponent } from 'utils/lazy-component'
import { NsfwPlatformHidden } from '@repo/studio-config/nsfw-platform-hidden'
import { ComponentBoundary } from '@repo/studio-components-boundary'
import { useExtensionBannerEligibility } from '@repo/studio-components-subscription-offers/extension/banner/eligibility'
import { AiVideoPromoBanner } from './components/ai-video-promo'
import { useCoinSubscriptionBannerQuery } from './components/coin-subscription/use-coin-subscription-banner-query'
import { useLowCoinsBannerQuery } from './components/low-coins/use-low-coins-banner-query'
import { useOnHoldBannerQuery } from './components/on-hold/use-on-hold-banner-query'
import { useUndoCancellationBannerQuery } from './components/undo-cancellation/use-undo-cancellation-banner-query'
import { useLatchedWinner } from './use-latched-winner'
import type { LatchableCandidate } from './use-latched-winner'

const OnHoldBanner = lazyComponent(() => import('./components/on-hold').then(m => m.OnHoldBanner))
const UndoCancellationBanner = lazyComponent(() =>
  import('./components/undo-cancellation').then(m => m.UndoCancellationBanner),
)
const LowCoinsBanner = lazyComponent(() =>
  import('./components/low-coins').then(m => m.LowCoinsBanner),
)
const CoinSubscriptionBanner = lazyComponent(() =>
  import('./components/coin-subscription').then(m => m.CoinSubscriptionBanner),
)
const ExtensionBannerGate = lazyComponent(() =>
  import('@repo/studio-components-subscription-offers/extension/banner/gate').then(
    m => m.SubscriptionExtensionBannerGate,
  ),
)

const TeamWorkspaceNotice = lazyComponent(() =>
  import('@repo/studio-components-workspace-switcher/team-workspace-notice').then(
    m => m.TeamWorkspaceNotice,
  ),
)

export function StudioBanners(): React.ReactNode {
  const teamWorkspacesEnabled = useFeatureIsOn('studio-team-workspaces')
  return (
    <>
      <NsfwPlatformHidden>
        <AiVideoPromoBanner />
      </NsfwPlatformHidden>
      <PriorityBanners />
      {teamWorkspacesEnabled && process.env.VITE_RPC_TEAMS_URL ? (
        <ComponentBoundary fallbackLoading={null}>
          <TeamWorkspaceNotice.component />
        </ComponentBoundary>
      ) : null}
    </>
  )
}

// One top-banner slot, highest priority first: account/retention state, then the subscription
// extension upsell, then the coin upsells.
function PriorityBanners() {
  const onHoldQuery = useOnHoldBannerQuery()
  const undoCancellationQuery = useUndoCancellationBannerQuery()
  const extensionBanner = useExtensionBannerEligibility()
  const coinSubscriptionQuery = useCoinSubscriptionBannerQuery()
  const lowCoinsQuery = useLowCoinsBannerQuery()

  const winner = useLatchedWinner<LatchableCandidate>(releaseLatch => [
    {
      id: 'onHold',
      isPending: onHoldQuery.isPending,
      isEligible: Boolean(onHoldQuery.data),
      render: () => (
        <ComponentBoundary fallbackLoading={null}>
          <OnHoldBanner.component />
        </ComponentBoundary>
      ),
    },
    {
      id: 'undoCancellation',
      isPending: undoCancellationQuery.isPending,
      isEligible: Boolean(undoCancellationQuery.data),
      render: () =>
        undoCancellationQuery.data ? (
          <ComponentBoundary fallbackLoading={null}>
            <UndoCancellationBanner.component {...undoCancellationQuery.data} />
          </ComponentBoundary>
        ) : null,
    },
    {
      id: 'extension',
      isPending: extensionBanner.isPending,
      isEligible: extensionBanner.isEligible,
      latchWhileActive: true,
      render: () => (
        <ComponentBoundary fallbackLoading={null}>
          <ExtensionBannerGate.component onDismiss={releaseLatch} />
        </ComponentBoundary>
      ),
    },
    {
      id: 'coinSubscription',
      isPending: coinSubscriptionQuery.isPending,
      isEligible: Boolean(coinSubscriptionQuery.data),
      render: () => (
        <ComponentBoundary fallbackLoading={null}>
          <CoinSubscriptionBanner.component />
        </ComponentBoundary>
      ),
    },
    {
      id: 'lowCoins',
      isPending: lowCoinsQuery.isPending,
      isEligible: Boolean(lowCoinsQuery.data),
      render: () => (
        <ComponentBoundary fallbackLoading={null}>
          <LowCoinsBanner.component />
        </ComponentBoundary>
      ),
    },
  ])

  return winner ? winner.render() : null
}
